import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, InputGroup, Pagination, OverlayTrigger, Tooltip, Modal, ProgressBar, Alert } from 'react-bootstrap';
import { FaSearch, FaFilter, FaPlus, FaEye, FaCheckCircle, FaBan, FaAngleLeft, FaAngleRight, FaUsers, FaExclamationTriangle, FaSpinner, FaBuilding, FaUser, FaFileContract } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { dsoApi } from '../../services/api.js';

const DSOList = () => {
  // Use navigate for routing
  const navigate = useNavigate();
  // Get location to access query parameters
  const location = useLocation();
  
  // Function to navigate to details page
  const viewDetails = (id) => {
    navigate(`/admin/dso/${id}/edit`);
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [stageFilter, setStageFilter] = useState('All');
  const [dsoList, setDsoList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Modal state
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [selectedDsoId, setSelectedDsoId] = useState(null);
  
  // Modal handlers
  const openVerifyModal = (id) => {
    setSelectedDsoId(id);
    setShowVerifyModal(true);
  };
  
  const openDeactivateModal = (id) => {
    setSelectedDsoId(id);
    setShowDeactivateModal(true);
  };
  
  const handleVerify = () => {
    // In a real app, this would call an API to verify the DSO
    console.log(`Verifying DSO ID: ${selectedDsoId}`);
    setShowVerifyModal(false);
    // Example of updating the local state
    const updatedList = dsoList.map(dso => 
      dso.id === selectedDsoId ? { ...dso, status: 'Verified', stage: '' } : dso
    );
    setDsoList(updatedList);
  };
  
  const handleDeactivate = async (id) => {
    // Use the passed id or the selectedDsoId from modal
    const dsoId = id || selectedDsoId;
    
    try {
      // Call API to deactivate the DSO
      await dsoApi.toggleDsoStatus(dsoId, false);
      
      // Close modal if it was opened
      if (!id) setShowDeactivateModal(false);
      
      // Update the local state
      const updatedList = dsoList.map(dso => 
        dso.id === dsoId ? { ...dso, status: 'Deactive' } : dso
      );
      setDsoList(updatedList);
      
      // Refresh the list to get updated data from server
      fetchDSOList();
    } catch (error) {
      console.error("Error deactivating DSO:", error);
      // Show error message if needed
    }
  };

  const handleActivate = async (id) => {
    const dsoId = id; // Directly use the passed ID

    try {
      // Call API to activate the DSO (set active=true)
      await dsoApi.toggleDsoStatus(dsoId, true);

      // Update the local state optimistically
      const updatedList = dsoList.map(dso =>
        dso.id === dsoId ? { ...dso, status: 'Active' } : dso // Assuming reactivation sets status to Active
      );
      setDsoList(updatedList);

      // Refresh the list to get updated data from server
      fetchDSOList();
    } catch (error) {
      console.error("Error activating DSO:", error);
      // Show error message if needed
    }
  };


  // Apply stage filter locally (since our API doesn't support it yet)
  const filteredByStage = stageFilter !== 'All'
    ? dsoList.filter(dso => dso.stage === stageFilter)
    : dsoList;
  
  // Fetch DSO list from API using dsoApi client
  const fetchDSOList = async () => {
    setLoading(true);
    setError('');
    try {
      // Check if there's a rm_id in the query parameters
      const urlParams = new URLSearchParams(location.search);
      const rmIdParam = urlParams.get('rm_id');
      
      // Build parameters for the API call
      const params = {
        skip: (currentPage - 1) * entriesPerPage,
        limit: entriesPerPage
      };
      
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }
      
      if (statusFilter !== 'All') {
        params.status = statusFilter;
      }
      
      // Add rm_id parameter if it exists in the URL
      if (rmIdParam) {
        params.rm_id = parseInt(rmIdParam);
      }
      
      // Use the dsoApi client to fetch the data
      const response = await dsoApi.getDsoList(params);
      
      setDsoList(response.data.items);
      setTotalItems(response.data.total);
      setTotalPages(Math.ceil(response.data.total / entriesPerPage));
      
    } catch (err) {
      console.error("Error fetching DSO list:", err);
      setError('Failed to load DSO list. Please try again later.');
      // Keep the old data if there's an error
    } finally {
      setLoading(false);
    }
  };
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Fetch data when pagination, search, or filters change
  useEffect(() => {
    fetchDSOList();
  }, [currentPage, entriesPerPage, debouncedSearchTerm, statusFilter]);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, entriesPerPage]);
  
  // Get current entries - use the data from the API directly
  const currentEntries = dsoList;
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Generate page numbers
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }
  
  // Limit visible page numbers (show max 3 pages with prev/next)
  const getVisiblePageNumbers = () => {
    if (totalPages <= 3) {
      return pageNumbers;
    }
    
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    
    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    
    return [currentPage - 1, currentPage, currentPage + 1];
  };

  return (
    <Container fluid className="p-3">
      <Row className="mb-2">
        <Col>
          <h4 className="mb-1">DSO List</h4>
          <p className="text-muted small mb-1">Manage DSO partners in the system</p>
        </Col>
      </Row>
      
      <Row className="mb-2">
        <Col md={4} lg={4}>
          <InputGroup className="mb-2">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search by email or phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2} lg={2}>
          <InputGroup className="mb-2">
            <InputGroup.Text title="Status Filter"><FaFilter /></InputGroup.Text>
            <Form.Select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="All">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Verified">Verified</option>
              <option value="Deactive">Deactive</option>
            </Form.Select>
          </InputGroup>
        </Col>
        <Col md={3} lg={3}>
          <InputGroup className="mb-2">
            <InputGroup.Text title="Stage Filter"><FaFilter /></InputGroup.Text>
            <Form.Select 
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              aria-label="Filter by stage"
            >
              <option value="All">All Stages</option>
              <option value="Agreement not signed">Agreement not signed</option>
              <option value="Documents Uploaded">Documents Uploaded</option>
              <option value="Agreement Signed">Agreement Signed</option>
            </Form.Select>
          </InputGroup>
        </Col>
        <Col className="d-flex justify-content-end">
          <Button variant="primary" onClick={() => navigate('/admin/dso-list/add')}>
            <FaPlus className="me-2" /> Add New DSO
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Table striped hover responsive size="sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>DSO Details</th>
                    <th>Location</th>
                    <th>Documents</th>
                    <th>Agents</th>
                    <th>Status</th>
                    <th>Stage</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        <FaSpinner className="fa-spin me-2" />
                        Loading DSO list...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4 text-danger">
                        <FaExclamationTriangle className="me-2" />
                        {error}
                      </td>
                    </tr>
                  ) : filteredByStage.length > 0 ? (
                    filteredByStage.map((dso) => (
                      <tr 
                        key={dso.id}
                        className={dso.status === 'Deactive' ? 'table-danger' : ''}
                      >
                        <td>{dso.id}</td>
                        <td>
                          <div className="d-flex align-items-center mb-2">
                            <div style={{ minWidth: '20px' }} className="me-2">
                              {dso.dso_type === 'Business' ? 
                                <FaBuilding className="text-primary" /> : 
                                <FaUser className="text-primary" />
                              }
                            </div>
                            <div>
                              <a 
                                href="#" 
                                onClick={(e) => {e.preventDefault(); viewDetails(dso.id);}}
                                className="text-decoration-none fw-bold"
                                style={{ cursor: 'pointer', color: '#4169E1' }}
                              >
                                {dso.name}
                              </a>
                              <span className="ms-2 badge bg-light text-dark">
                                {dso.dso_type}
                              </span>
                            </div>
                          </div>
                          <div className="ps-4 small text-muted">
                            {dso.email}
                          </div>
                          <div className="ps-4 small text-muted">
                            {dso.phone}
                          </div>
                        </td>
                        <td>{dso.location}</td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center">
                            {dso.dso_type === 'Individual' ? (
                              <>
                              <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`tooltip-view-${dso.id}`}>PAN Card</Tooltip>}
                            >
                                <span 
                                  className="me-2 fw-bold" 
                                  title="PAN Card" 
                                  style={{ 
                                    color: dso.has_pan ? '#4eb543' : '#aaa',
                                    fontSize: '18px'
                                  }}
                                >
                                  P
                                </span>
                                </OverlayTrigger>
                                <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`tooltip-view-${dso.id}`}>Aadhaar Card</Tooltip>}
                            >
                                <span 
                                  className="fw-bold" 
                                  title="Aadhaar Card" 
                                  style={{ 
                                    color: dso.has_aadhaar ? '#4eb543' : '#aaa',
                                    fontSize: '18px'
                                  }}
                                >
                                  A
                                </span>
                                </OverlayTrigger>
                              </>
                            ) : (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`tooltip-view-${dso.id}`}>Company Certificate</Tooltip>}
                            >
                              <span 
                                className="fw-bold" 
                                title="Company Certificate" 
                                style={{ 
                                  color: dso.has_company_cert ? '#4eb543' : '#aaa',
                                  fontSize: '18px'
                                }}
                              >
                                <FaFileContract />
                              </span>
                              </OverlayTrigger>
                            )}
                          </div>
                        </td>
                        <td className="text-center">
                          {dso.agents > 0 ? (
                            <span className="badge bg-info">{dso.agents}</span>
                          ) : (
                            <span className="badge bg-secondary">0</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${
                            dso.status === 'Verified' ? 'bg-success' : 
                            dso.status === 'Pending' ? 'bg-warning text-dark' : 
                            dso.status === 'Deactive' ? 'bg-danger' : 'bg-primary'
                          }`}>
                            {dso.status}
                          </span>
                        </td>
                        <td>
                          {dso.stage && (
                            <span className={`badge ${
                              dso.stage === 'Agreement Signed' ? 'bg-info text-dark' : 
                              dso.stage === 'Documents Uploaded' ? 'bg-secondary' : 
                              dso.stage === 'Agreement not signed' ? 'bg-warning text-dark' : 'bg-light text-dark'
                            }`}>
                              {dso.stage}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {/* View Details button - always visible */}
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`tooltip-view-${dso.id}`}>View Details</Tooltip>}
                            >
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="py-0 px-1" 
                                onClick={() => viewDetails(dso.id)}
                              >
                                <FaEye />
                              </Button>
                            </OverlayTrigger>

                            
                            {/* Deactivation and reset buttons */}
                            {dso.status === 'Deactive' ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`tooltip-activate-${dso.id}`}>Activate DSO</Tooltip>}
                              >
                                <Button 
                                  variant="outline-success" // Change variant to success
                                  size="sm" 
                                  className="py-0 px-1"
                                  onClick={() => handleActivate(dso.id)} // Call handleActivate
                                >
                                  <FaCheckCircle /> {/* Change icon to FaCheckCircle */}
                                </Button>
                              </OverlayTrigger>
                            ) : dso.status !== 'Deactive' && (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`tooltip-deactivate-${dso.id}`}>Deactivate</Tooltip>}
                              >
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  className="py-0 px-1"
                                  onClick={() => openDeactivateModal(dso.id)}
                                >
                                  <FaBan />
                                </Button>
                              </OverlayTrigger>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-3">
                        No DSO partners found matching the search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Pagination and Entries Per Page Controls */}
              <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
                <div className="mb-2">
                  <span>Show </span>
                  <Form.Select
                    style={{ width: 'auto', display: 'inline-block' }}
                    className="mx-2"
                    value={entriesPerPage}
                    onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </Form.Select>
                  <span> entries per page</span>
                </div>

                <Pagination className="mb-0">
                  <Pagination.Prev 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <FaAngleLeft />
                  </Pagination.Prev>
                  
                  {getVisiblePageNumbers().map(number => (
                    <Pagination.Item 
                      key={number} 
                      active={number === currentPage}
                      onClick={() => paginate(number)}
                    >
                      {number}
                    </Pagination.Item>
                  ))}
                  
                  <Pagination.Next 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    <FaAngleRight />
                  </Pagination.Next>
                </Pagination>

                <div className="mb-2 text-secondary">
                  Showing {currentEntries.length > 0 ? ((currentPage - 1) * entriesPerPage) + 1 : 0} to {((currentPage - 1) * entriesPerPage) + currentEntries.length} of {totalItems} entries
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Verify Confirmation Modal */}
      <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <div className="d-flex align-items-center">
              <FaCheckCircle className="me-2 text-primary" />
              <span>Verify DSO Partner</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <div className="mb-3 p-2 bg-light rounded">
              <FaCheckCircle size={48} className="text-primary mb-3" />
              <p className="mb-1 fw-bold">Verification Confirmation</p>
              <p className="mb-0">Are you sure you want to verify this DSO partner?</p>
            </div>
            <div className="alert alert-info small">
              <p className="mb-0">This will mark the partner as verified in the system. Once verified, the DSO can upload agents.</p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowVerifyModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleVerify}>
            Verify DSO
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deactivate Confirmation Modal */}
      <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <div className="d-flex align-items-center">
              <FaBan className="me-2 text-danger" />
              <span>Deactivate DSO Partner</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <div className="mb-3 p-2 bg-light rounded">
              <FaExclamationTriangle size={48} className="text-danger mb-3" />
              <p className="mb-1 fw-bold">Deactivation Confirmation</p>
              <p className="mb-0">Are you sure you want to deactivate this DSO partner?</p>
            </div>
            <div className="alert alert-warning small">
              <p className="mb-0">This will change the partner's status to deactive and will suspend their operations. To reactivate, use the "Reset to Pending" option.</p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDeactivateModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleDeactivate()}>
            Deactivate DSO
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default DSOList;
